// Collapsed admin strips under the board — collectors + suggested issues (Screen 3).
import { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import { Icon } from "../../../mui";
import { UnlabeledWorkPanel } from "../UnlabeledWorkPanel";
import { SuggestedIssuesPanel } from "../SuggestedIssuesPanel";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

function StripHeader({
  title,
  count,
  open,
  onToggle,
  inert = false,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  inert?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={inert}
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        textAlign: "left",
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        background: c.white,
        padding: "13px 18px",
        cursor: inert ? "default" : "pointer",
        fontFamily: brainTokens.font.sans,
        color: inert ? c.muted2 : c.ink2,
        "&:hover": inert ? undefined : { borderColor: c.hoverBorder },
      }}
    >
      <Box sx={{ display: "inline-flex", color: inert ? c.muted2 : c.faint, flex: "none" }}>
        <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
      </Box>
      <Box sx={{ fontSize: 13, fontWeight: 500 }}>{title}</Box>
      <Box
        sx={{
          fontFamily: MONO,
          fontSize: 11,
          color: c.muted2,
          background: c.panelBg2,
          borderRadius: `${brainTokens.radius.pill}px`,
          px: "7px",
          py: "1px",
          flex: "none",
        }}
      >
        {count}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box sx={{ fontSize: 11.5, color: c.faint, flex: "none" }}>admin</Box>
    </Box>
  );
}

function UnlabeledStrip({
  projectId,
  isAdmin,
  onChanged,
}: {
  projectId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const onCount = useCallback((n: number) => setCount(n), []);
  const inert = count === 0;

  return (
    <Box>
      <StripHeader
        title="Unlabeled work caught by collectors"
        count={count}
        open={open && !inert}
        inert={inert}
        onToggle={() => {
          if (!inert) setOpen((v) => !v);
        }}
      />
      {/* Always mounted so onCount stays fresh while collapsed */}
      <Box sx={{ display: open && !inert ? "block" : "none", pb: "8px", pl: "22px" }}>
        <UnlabeledWorkPanel
          projectId={projectId}
          isAdmin={isAdmin}
          onChanged={onChanged}
          embedded
          onCount={onCount}
        />
      </Box>
    </Box>
  );
}

function SuggestedStrip({ projectId }: { projectId: string }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const onCount = useCallback((n: number) => setCount(n), []);
  const inert = count === 0;

  return (
    <Box>
      <StripHeader
        title="Suggested issues"
        count={count}
        open={open && !inert}
        inert={inert}
        onToggle={() => {
          if (!inert) setOpen((v) => !v);
        }}
      />
      {/* Always mounted so onCount stays fresh while collapsed — single fetch */}
      <Box sx={{ display: open && !inert ? "block" : "none", pl: "22px", pb: "8px" }}>
        <SuggestedIssuesPanel projectId={projectId} embedded onCount={onCount} />
      </Box>
    </Box>
  );
}

export function AdminStrips({
  projectId,
  isAdmin,
  onChanged,
}: {
  projectId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  return (
    <Box sx={{ mt: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
      <UnlabeledStrip projectId={projectId} isAdmin={isAdmin} onChanged={onChanged} />
      <SuggestedStrip projectId={projectId} />
    </Box>
  );
}
